import { getApiUrl } from '@/lib/api-config';

/**
 * The commercial invitation of a client / vendor contact (backend: modules/commercial-invitations). "Save" on the contact form saves the contact only; "Send Invitation"
 * saves it and then calls this. The answer is shown as the server gave it: nothing is reported as sent unless the email was really handed to the mail provider.
 */
export type InvitationStatus = 'none' | 'sent' | 'failed' | 'expired' | 'accepted' | 'revoked';

export interface InvitationState {
  status: InvitationStatus;
  invitable?: boolean;
  email?: string;
  last_sent_at?: string;
  send_count?: number;
  last_send_error?: string | null;
  expires_at?: string;
  sent?: boolean;
  error?: string | null;
}

export interface InvitationNotice {
  title: string;
  description?: string;
  variant: 'default' | 'destructive';
}

async function call(path: string, method: 'GET' | 'POST'): Promise<InvitationState> {
  const response = await fetch(getApiUrl(path), { method, headers: { 'Content-Type': 'application/json' } });
  const json = await response.json().catch(() => null);
  if (!response.ok || !json?.success) throw new Error(json?.error || json?.message || 'The invitation could not be processed');
  return json.data as InvitationState;
}

export const fetchInvitationStatus = (personId: string) => call(`api/people/${personId}/commercial-invitation`, 'GET');
export const sendCommercialInvitation = (personId: string) => call(`api/people/${personId}/commercial-invitation`, 'POST');
export const revokeCommercialInvitation = (personId: string) => call(`api/people/${personId}/commercial-invitation/revoke`, 'POST');

export function statusLabel(state: InvitationState | null): string {
  switch (state?.status) {
    case 'sent': return 'Invitation sent';
    case 'failed': return 'Invitation could not be delivered';
    case 'expired': return 'Invitation expired';
    case 'accepted': return 'Invitation accepted';
    case 'revoked': return 'Invitation cancelled';
    default: return 'Contact saved, no invitation sent';
  }
}

/** What to tell the user after Send Invitation: only a really sent email says "sent". */
export function noticeAfterSend(result: InvitationState | null, error: unknown, contactName: string): InvitationNotice {
  if (error || !result) {
    return {
      title: `${contactName} was saved, but the invitation was not sent`,
      description: `${error instanceof Error ? error.message : 'Something went wrong.'} Open the contact and use Send Invitation to try again.`,
      variant: 'destructive',
    };
  }
  if (!result.sent) {
    return {
      title: `${contactName} was saved, but the email could not be sent`,
      description: `${result.error || 'The mail provider did not accept the message.'} Open the contact and use Send Invitation to try again.`,
      variant: 'destructive',
    };
  }
  return { title: `Invitation sent to ${result.email || contactName}`, description: 'They will create a free account from the link in the email. It gives them no access until you add them to a project.', variant: 'default' };
}
